import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, ArrowRight } from "lucide-react";
import { usePageNavigate } from "../hooks/usePageNavigate";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let id = sessionStorage.getItem("ic_chat_session");
  if (!id) {
    id = "chat_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem("ic_chat_session", id);
  }
  return id;
}

export default function ChatWidget({ user }) {
  const { navigateTo } = usePageNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: user
      ? `Welcome back, ${user.full_name.split(" ")[0]}! I'm Gina, your Imaani Capital advisor. I can see your applications and help with deal analysis, loan programs, or next steps. What can I help with?`
      : "Hey there! I'm Gina, your Imaani Capital advisor. I can help you understand our loan programs, the application process, and real estate financing. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const sendMessage = useCallback(async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setLoading(true);
    try {
      const headers = {};
      const token = localStorage.getItem("ic_token");
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(`${API}/chat/message`, {
        message: text,
        session_id: sessionId.current,
      }, { headers });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I'm having trouble connecting right now. Please try again or call us at (832) 859-4179." }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            onClick={() => setOpen(true)}
            data-testid="chat-widget-toggle"
            className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-gradient-to-br from-emerald-700 to-emerald-900 text-white shadow-lg shadow-emerald-900/30 flex items-center justify-center hover:from-emerald-600 hover:to-emerald-800 transition-all group"
          >
            <MessageCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
            <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-gold animate-pulse" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            data-testid="chat-panel"
            className="fixed bottom-6 right-6 z-50 w-[380px] max-h-[560px] rounded-2xl border border-cream-warm bg-white shadow-2xl shadow-emerald-900/15 flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-800 to-emerald-900 px-5 py-4 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-sm font-bold text-gold">
                  G
                </div>
                <div>
                  <div className="text-sm font-bold text-cream">Gina</div>
                  <div className="text-[10px] text-cream/60 uppercase tracking-wider">Deal Advisor</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="chat-close"
                className="p-1.5 rounded-lg text-cream/60 hover:text-cream hover:bg-white/10 transition-colors">
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0" data-testid="chat-messages">
              {messages.map((msg, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    msg.role === "user"
                      ? "bg-emerald-700 text-white rounded-br-md"
                      : "bg-cream border border-cream-warm text-[#333] rounded-bl-md"
                  }`}>
                    {msg.content}
                  </div>
                </motion.div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-cream border border-cream-warm rounded-2xl rounded-bl-md px-4 py-3">
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                      <div className="w-2 h-2 rounded-full bg-emerald-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                    </div>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* Quick Actions for visitors */}
            {!user && messages.length <= 2 && (
              <div className="px-4 pb-2 flex flex-wrap gap-1.5 shrink-0">
                {["What loan programs do you offer?", "How does the application work?", "What rates can I expect?"].map(q => (
                  <button key={q} onClick={() => { setInput(q); setTimeout(sendMessage, 50); }}
                    className="text-xs px-3 py-1.5 rounded-full border border-cream-warm bg-cream text-[#555] hover:border-emerald-300 hover:text-emerald-700 transition-colors">
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* CTA for non-logged-in users */}
            {!user && messages.length > 3 && (
              <div className="px-4 pb-2 shrink-0">
                <button onClick={() => { setOpen(false); navigateTo("apply"); }}
                  data-testid="chat-apply-cta"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-700 to-emerald-800 text-white text-sm font-semibold hover:from-emerald-600 hover:to-emerald-700 transition-all">
                  Submit an Application for Personalized Analysis <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            )}

            {/* Input */}
            <div className="border-t border-cream-warm p-3 flex items-center gap-2 shrink-0 bg-white">
              <input
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about loans, rates, or your deal..."
                disabled={loading}
                data-testid="chat-input"
                className="flex-1 text-sm px-3 py-2.5 rounded-xl border border-cream-warm bg-cream text-[#0A0A0A] placeholder:text-[#999] focus:outline-none focus:border-emerald-400 focus:ring-1 focus:ring-emerald-400/30 transition-colors"
              />
              <button onClick={sendMessage} disabled={loading || !input.trim()} data-testid="chat-send"
                className="p-2.5 rounded-xl bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
